/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

/**
 * Clip Relationships API Client
 *
 * Flexible relationship system - any clip can relate to any other clip.
 * The meaning comes from:
 * - Tags on clips (dailies, storyboard, vfx-shot, vfx-version, etc.)
 * - Metadata stored with relationships (frame ranges, notes, etc.)
 * - Future VFX tracking system
 *
 * Automatically injects Supabase JWT tokens for authentication.
 */

import { supabase } from '@/lib/supabase'

// =============================================
// TYPES (matching backend schemas)
// =============================================

export interface ClipRelationshipCreateRequest {
  target_clip_id: string
  relationship_type?: string  // Flexible - defaults to 'related'
  metadata?: Record<string, any>
}

export interface ClipInfo {
  id: string
  name: string
  nle_unique_id?: string
  project_id: string
  tags?: string[]
}

export interface ClipRelationshipResponse {
  id: string
  source_clip_id: string
  target_clip_id: string
  relationship_type: string  // Flexible
  metadata: Record<string, any>
  created_by: string
  created_at: string
  source_clip?: ClipInfo
  target_clip?: ClipInfo
}

export interface ClipRelationshipListResponse {
  clip_id: string
  relationships: ClipRelationshipResponse[]
  total: number
}

export interface ClipRelationshipGraphNode {
  clip_id: string
  clip_name: string
  relationship_type?: string  // Flexible
  direction: 'root' | 'outgoing' | 'incoming'
  depth: number
}

export interface ClipRelationshipGraphResponse {
  root_clip_id: string
  nodes: ClipRelationshipGraphNode[]
  total_nodes: number
}

// =============================================
// API CLIENT CONFIGURATION
// =============================================

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000'

/**
 * Get the current Supabase JWT token for authentication
 */
async function getAuthToken(): Promise<string> {
  const { data: { session }, error } = await supabase.auth.getSession()

  if (error) {
    console.error('Auth error:', error)
    throw new Error('Authentication error. Please try logging in again.')
  }

  if (!session) {
    throw new Error('Not authenticated. Please log in.')
  }

  return session.access_token
}

/**
 * Make an authenticated API request to the backend
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAuthToken()

  const url = `${BACKEND_URL}${endpoint}`

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }))
    console.error(`[API] Error ${response.status}:`, errorData)

    if (response.status === 401) {
      throw new Error('Authentication failed. Please check:\n1. Backend is running\n2. SUPABASE_JWT_SECRET is configured correctly in backend/.env\n3. You are logged in')
    }

    throw new Error(errorData.detail || `API error: ${response.statusText}`)
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return null as T
  }

  return await response.json()
}

// =============================================
// API METHODS
// =============================================

/**
 * Create a new relationship between two clips
 *
 * @param sourceClipId UUID of the source clip
 * @param data Relationship details
 * @returns The created relationship
 */
export async function createClipRelationship(
  sourceClipId: string,
  data: ClipRelationshipCreateRequest
): Promise<ClipRelationshipResponse> {
  return apiRequest<ClipRelationshipResponse>(
    `/api/clips/${sourceClipId}/relationships`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  )
}

/**
 * List all relationships for a clip (bidirectional)
 *
 * @param clipId UUID of the clip
 * @param relationshipType Optional filter by relationship type (flexible - can be any string)
 * @returns List of relationships
 */
export async function listClipRelationships(
  clipId: string,
  relationshipType?: string
): Promise<ClipRelationshipListResponse> {
  const params = new URLSearchParams()
  if (relationshipType) params.append('relationship_type', relationshipType)

  const queryString = params.toString()
  const endpoint = `/api/clips/${clipId}/relationships${queryString ? `?${queryString}` : ''}`

  return apiRequest<ClipRelationshipListResponse>(endpoint)
}

/**
 * Delete a clip relationship
 *
 * @param clipId UUID of the clip (source or target)
 * @param relationshipId UUID of the relationship to delete
 */
export async function deleteClipRelationship(
  clipId: string,
  relationshipId: string
): Promise<void> {
  return apiRequest<void>(
    `/api/clips/${clipId}/relationships/${relationshipId}`,
    {
      method: 'DELETE',
    }
  )
}

/**
 * Get the full relationship graph starting from a clip
 *
 * @param clipId UUID of the root clip
 * @param maxDepth Maximum depth to traverse (default 10)
 * @returns Graph of relationships
 */
export async function getClipRelationshipGraph(
  clipId: string,
  maxDepth: number = 10
): Promise<ClipRelationshipGraphResponse> {
  const params = new URLSearchParams()
  params.append('max_depth', maxDepth.toString())

  return apiRequest<ClipRelationshipGraphResponse>(
    `/api/clips/${clipId}/relationships/graph?${params.toString()}`
  )
}

/**
 * Check if the backend is reachable
 *
 * @returns True if backend is healthy
 */
export async function checkBackendHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${BACKEND_URL}/health`)
    return response.ok
  } catch {
    return false
  }
}

// =============================================
// HELPER FUNCTIONS
// =============================================

/**
 * Get relationship label for display
 * Flexible - displays the type as-is or provides friendly labels for common types
 */
export function getRelationshipLabel(type: string): string {
  const commonLabels: Record<string, string> = {
    'related': 'Related',
    'source': 'Source',
    'version': 'Version',
    'reference': 'Reference',
    'storyboard': 'Storyboard'
  }
  return commonLabels[type] || type
}

/**
 * Get relationship icon
 * Provides icons for common relationship types
 */
export function getRelationshipIcon(type: string): string {
  const icons: Record<string, string> = {
    'related': '↔',
    'source': '↑',
    'version': '→',
    'reference': '⤷',
    'storyboard': '🎨'
  }
  return icons[type] || '•'
}
